import React, { useCallback, useEffect, useRef } from 'react'
import {
  MangoButton,
  DataTable,
  Inline,
  Stack,
  TDataTableColumn,
  TextStyle,
  MangoIconButton,
} from '@intellihr/blueberry'
import { Date } from 'src/services/uiComponentCandidates/Date'
import { useScope } from 'src/services/i18n/LocalizationProvider'
import { DocumentUploadStatus, PersonDocument, PersonDocumentUploadStatus } from 'src/__gql__/globalTypes'
import { DropzoneWrapper } from 'src/domain/EDM/internal/components/pageContents/PersonProfilePageContent/PersonProfilePageContentBody/tabs/PersonProfileDocumentsTab/components/DocumentDropzoneWrapper'
import {
  ICreateDocumentVariables,
  ISetDocumentUploadStatusVariables,
  useUploadDocument,
} from 'src/services/documents/hooks/useUploadDocument'
import { EmptyState, Variables } from '@intellihr/ui-components'
import { useOnboardingQualificationInstanceAttachmentCreate } from '../../../mutations/useOnboardingQualificationInstanceAttachmentCreate'
import { useOnboardingQualificationInstanceAttachmentUpdate } from '../../../mutations/useOnboardingQualificationInstanceAttachmentUpdate'
import { useOnboardingQualificationInstanceAttachmentDelete } from '../../../mutations/useOnboardingQualificationInstanceAttachmentDelete'

interface ICreateQualificationAttachmentForOnboardingParameters {
  variables: {
    qualificationInstanceId: string
    filename: string
    mime: string
    size: number
    extension: string
  }
  refetchQueries?: string[]
}

interface IUpdateQualificationAttachmentForOnboardingParameters {
  variables: {
    documentId: string
    displayName?: string
    uploadStatus: PersonDocumentUploadStatus
    folderId: string
  }
  refetchQueries?: string[]
}

interface IDeleteQualificationAttachmentForOnboardingParameters {
  variables: {
    id: string
  }
  refetchQueries?: string[]
}

interface IQualificationInstanceAttachmentForOnboardingProps {
  qualificationInstanceId: string
  attachments: PersonDocument[]
  setAttachments: React.Dispatch<React.SetStateAction<PersonDocument[]>>
  setAttachmentUploading: (value: boolean) => void
}

const QualificationInstanceAttachmentForOnboarding: React.FC<IQualificationInstanceAttachmentForOnboardingProps> = ({
  qualificationInstanceId,
  attachments,
  setAttachments,
  setAttachmentUploading,
}) => {
  const tFeature = useScope('qualifications:components.QualificationInstancePage')
  const tCommon = useScope('common:components')
  const dropzoneInputRef = useRef<React.RefObject<HTMLInputElement> | null>(null)

  const [createQualificationAttachment, { loading: isCreatingQualificationAttachment }] =
    useOnboardingQualificationInstanceAttachmentCreate()
  const [updateQualificationAttachment, { loading: isUpdatingQualificationAttachment }] =
    useOnboardingQualificationInstanceAttachmentUpdate({
      successMessage: tCommon('uploadFile.successMessage'),
    })
  const [deleteQualificationAttachment, { loading: isDeletingQualificationAttachment }] =
    useOnboardingQualificationInstanceAttachmentDelete({
      successMessage: tFeature('deleteAttachmentSuccessMessage'),
    })

  const { currentlyUploadingDocuments, uploadDocument } = useUploadDocument({
    modelId: qualificationInstanceId,
    createDocument: useCallback(
      async ({ filename, extension, mime, size }: ICreateDocumentVariables) => {
        const response = await createQualificationAttachment({
          variables: {
            qualificationInstanceId,
            filename,
            mime,
            size,
            extension,
          },
        })

        if (response.data) {
          const { document, presignedUploadUrl } = response.data.onboardingQualificationInstanceAttachmentCreate
          return {
            document: {
              ...document,
              __typename: 'Document' as const,
              uploadStatus: document.uploadStatus as unknown as DocumentUploadStatus,
              size,
            },
            presignedUrl: presignedUploadUrl,
          }
        }
        return undefined
      },
      [createQualificationAttachment, qualificationInstanceId],
    ),
    setDocumentUploadStatus: useCallback(
      async ({ id, uploadStatus }: ISetDocumentUploadStatusVariables) => {
        const response = await updateQualificationAttachment({
          variables: {
            documentId: id,
            uploadStatus: uploadStatus as unknown as PersonDocumentUploadStatus,
            folderId: id,
          },
        })
        if (response.data) {
          const { document } = response.data.onboardingQualificationInstanceAttachmentUpdate
          setAttachments((prevAttachments: PersonDocument[]) => [...prevAttachments, document])
        }
      },
      [updateQualificationAttachment, setAttachments],
    ),
  })

  const isUploading =
    (currentlyUploadingDocuments && Object.keys(currentlyUploadingDocuments).length > 0) ||
    isCreatingQualificationAttachment ||
    isUpdatingQualificationAttachment ||
    isDeletingQualificationAttachment

  useEffect(() => {
    setAttachmentUploading(isUploading)
  }, [isUploading, setAttachmentUploading])

  const handleAttachmentDelete = async (id: string) => {
    const response = await deleteQualificationAttachment({
      variables: { id },
    })

    if (response.data) {
      setAttachments((prevAttachments: PersonDocument[]) =>
        prevAttachments.filter((attachment) => attachment.id !== id),
      )
    }
  }

  const columns: Array<TDataTableColumn<PersonDocument>> = [
    {
      accessorKey: 'displayName',
      header: `${tFeature('input.file')}`,
    },
    {
      accessorKey: 'humanReadableSize',
      header: tFeature('input.fileSize'),
    },
    {
      accessorKey: 'createdAtDateTime',
      header: `${tFeature('input.dateAdded')}`,
      cell: ({ row }) => {
        return (
          <Date
            date={row.original.createdAtDateTime}
            fallback=""
            showTime={true}
          />
        )
      },
    },
    {
      accessorKey: 'viewAction',
      header: '',
      meta: { isNarrow: true },
      cell: ({ row }) => {
        return (
          <MangoButton
            variant="tertiary"
            size="small"
            href={row.original.downloadUrlAbsolute}
            download
          >
            {tCommon('buttons.view')}
          </MangoButton>
        )
      },
    },
    {
      accessorKey: 'deleteAction',
      header: '',
      meta: { isNarrow: true },
      cell: ({ row }) => {
        return (
          <MangoIconButton
            variant="destructive"
            size="small"
            icon="delete"
            label={tCommon('buttons.delete')}
            onClick={() => {
              handleAttachmentDelete(row.original.id)
            }}
          />
        )
      },
    },
  ]

  const hasAttachments = attachments.length > 0

  return (
    <Stack>
      <Inline alignY="center">
        <TextStyle variant="strong">{tFeature('input.attachments')}</TextStyle>
        <MangoButton
          size="small"
          variant="secondary"
          onClick={() => dropzoneInputRef.current?.current?.click()}
          loading={isUploading}
        >
          {tCommon('uploadFile.uploadFile')}
        </MangoButton>
      </Inline>
      <div style={{ marginBottom: Variables.Spacing.s2XLarge }}>
        {hasAttachments ? (
          <DropzoneWrapper
            dropzoneInputRef={dropzoneInputRef}
            hasDocuments={hasAttachments}
            uploadDocument={uploadDocument}
          >
            <DataTable
              columns={columns}
              data={attachments}
              emptyStateTitle={''}
              isLoading={isUploading}
            />
          </DropzoneWrapper>
        ) : (
          <DropzoneWrapper
            dropzoneInputRef={dropzoneInputRef}
            hasDocuments={hasAttachments}
            uploadDocument={uploadDocument}
          >
            <EmptyState
              primaryMessage={tFeature('attachmentEmptyStatePrimaryMessage')}
              secondaryMessage={tFeature('attachmentEmptyStateSecondaryMessage')}
              isBackgroundTransparent
              margins={{ bottom: Variables.Spacing.sXLarge }}
            />
          </DropzoneWrapper>
        )}
      </div>
    </Stack>
  )
}

export type {
  ICreateQualificationAttachmentForOnboardingParameters,
  IUpdateQualificationAttachmentForOnboardingParameters,
  IDeleteQualificationAttachmentForOnboardingParameters,
}
export { QualificationInstanceAttachmentForOnboarding }
